import { DocumentNode } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import { ApiKey } from "types/generated";
import { stringIsRequired } from "utils/formValidators";

import { PersonalSettingsApiKeysEditFields } from "./types";
import usePersonalApiKeysUpdate from "./useUpdate";

type PersonalSettingsApiKeysEditDrawerProps = {
  apiKey: Pick<ApiKey, "id" | "description" | "name">;
  refetchQueries?: DocumentNode[];
};

const PersonalSettingsApiKeysEditDrawer = createDrawer(
  ({ apiKey, refetchQueries }: PersonalSettingsApiKeysEditDrawerProps) => {
    const drawer = useModal();

    const handleCloseDrawer = () => {
      drawer.resolve();
      drawer.hide();
    };

    const { onUpdate, loading: isSubmitting } = usePersonalApiKeysUpdate({
      callback: handleCloseDrawer,
      refetchQueries,
    });

    const editForm = useForm<PersonalSettingsApiKeysEditFields>({
      defaultValues: {
        name: apiKey.name || "",
        description: apiKey.description || "",
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = editForm;

    const onSubmit: SubmitHandler<PersonalSettingsApiKeysEditFields> = (formFields) => {
      onUpdate(apiKey.id, {
        name: formFields.name.trim(),
        description: formFields.description.trim(),
        teams: null,
        oidcClaimMappings: null,
      });
    };

    const handleSubmitClick = () => {
      handleSubmit(onSubmit)();
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...editForm}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Edit details" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <FormField label="Name" error={errors.name?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter API key name"
                  error={!!errors.name}
                  {...register("name", {
                    validate: stringIsRequired("Name field is required."),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter API key description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <DrawerFooter>
              <DrawerFooterActions>
                <Button variant="secondary" onPress={handleCloseDrawer}>
                  Cancel
                </Button>
                <Button variant="primary" onPress={handleSubmitClick} loading={isSubmitting}>
                  Save
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showPersonalSettingsApiKeysEditDrawer = createDrawerTrigger(
  PersonalSettingsApiKeysEditDrawer
);
